import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectSharedGameState} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectRoundStandingsState} from "../../../redux/reducers/privateStateReducer/selectors";
import {selectIsRoundEndModalVisible} from '../../../redux/selectors/modalVisibility';
import './RoundEndModal.css';
import {hideRoundEndModal} from "../../../redux/actions";
import ModalWindow from "../ModalWindow/ModalWindow";
import {useEffectOnPositiveChange} from "../../../hooks/react/useEffectOnPositiveChange";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import ModalButton from "../ModalWindow/ModalButton/ModalButton";
import TutorialModalExplanation from "../../Tutorial/TutorialModalExplanation/TutorialModalExplanation";
import {MINUS_SIGN} from "../../../constants/text";
import {InlineTokenIcon} from "../../Tutorial/modals/chapter1Modals";
import {buildRoundStandings} from "../../../utils/gameEnd/buildRoundStandings";
import RoundEndStandings from "./RoundEndStandings";

/**
 * Строки таблицы итогов раунда: игроки в порядке мест по итоговым очкам (как
 * в итогах партии) и их сдвиг относительно прошлого раунда
 */
function buildRoundEndRows(gameState, roundStandings) {
  const {round, players, startedAt} = gameState;
  const endedRound = round - 1;
  // Слепок той же партии: полная загрузка состояния (ресинк, возврат вкладки
  // из фона, просмотр записи) слепки не сбрасывает, поэтому партия
  // сверяется по моменту ее старта
  const isSnapshotOfThisMatch = (snapshot) => snapshot?.startedAt === startedAt;
  const hasCurrentSnapshot = isSnapshotOfThisMatch(roundStandings.current)
    && roundStandings.current.round === endedRound;
  // Слепок пишется в момент смены раунда (см. modalsMiddleware). Если его нет
  // (переподключились прямо на модалке) — итоги считаются по живым данным
  const current = hasCurrentSnapshot
    ? roundStandings.current.standings
    : buildRoundStandings(gameState);
  const previousSnapshot = hasCurrentSnapshot ? roundStandings.previous : null;
  // Сравниваем только с непосредственно предыдущим раундом и тем же составом
  const previous = isSnapshotOfThisMatch(previousSnapshot)
    && previousSnapshot.round === endedRound - 1
    && previousSnapshot.standings.length === current.length
    && current.every((standing) => previousSnapshot.standings.some(({id}) => id === standing.id))
    ? previousSnapshot.standings
    : null;

  return current.map((standing) => {
    const previousIndex = previous ? previous.findIndex(({id}) => id === standing.id) : null;
    return {
      player: players.find(({id}) => id === standing.id),
      standing,
      previousIndex,
      // Место прошлого раунда: в старых слепках поля нет — тогда по порядку
      previousPlace: previous ? (previous[previousIndex].place ?? previousIndex + 1) : null,
      finalWinPointsDelta: previous
        ? standing.finalWinPoints - previous[previousIndex].finalWinPoints
        : null,
    };
  });
}

const RoundEndModal = () => {
  const dispatch = useDispatch();
  const gameState = useSelector(selectSharedGameState);
  // Именно ВИДИМЫЕ модалки боя и захвата, а не флаги их показа: залипший
  // флаг боя с пустыми результатами прятал итоги раунда до следующего боя
  // (см. redux/selectors/modalVisibility.js)
  const showModalFinal = useSelector(selectIsRoundEndModalVisible);
  const roundStandings = useSelector(selectRoundStandingsState);

  const {round} = gameState;

  useEffectOnPositiveChange(showModalFinal, () => {
    const soundManager = REGISTRY_SINGLETON.getSoundManager();
    soundManager.playModalRoundEnd();
  });

  if (!showModalFinal) {
    return null;
  }

  const handleClose = () => {
    dispatch(hideRoundEndModal());
  };

  return (
    <ModalWindow
      handleClose={handleClose}
      className={'round-end-modal'}
      showQuote={true}
    >
      <div className="round-end-modal-header">
        <h2>Раунд {round - 1} завершён!</h2>
      </div>

      <TutorialModalExplanation>
        В конце раунда каждому игроку начисляется доход.
        <br/>Монеты<InlineTokenIcon
        src={'/img/icons/coins.png'} alt={'Монеты'}
      /> {MINUS_SIGN} за Рудники<InlineTokenIcon
        src={'/img/hexes/mine.ecae90cd.png'} alt={'Рудник'}
      /><InlineTokenIcon
        src={'/img/hexes/super_mine.7a4a81f7.png'} alt={'Великий Рудник'}
      /> и Гильдии<InlineTokenIcon src={'/img/hexes/special buildings/guild.ee26d2b8.png'} alt={'Гильдия'}/> игрока.
        <br/>Победные очки<InlineTokenIcon
        src={'/img/icons/win points.png'} alt={'Победные очки'}
      /> {MINUS_SIGN} за Руины<InlineTokenIcon
        src={'/img/hexes/ruins.65862e11.png'} alt={'Руины'}
      /><InlineTokenIcon
        src={'/img/hexes/super_ruins.97515170.png'} alt={'Великие Руины'}
      /> и Храмы<InlineTokenIcon src={'/img/hexes/special buildings/temple.17c08b3d.png'} alt={'Храм'}/>.
      </TutorialModalExplanation>
      <div className="round-end-modal-content">
        <RoundEndStandings rows={buildRoundEndRows(gameState, roundStandings)}/>
      </div>

      <div className="round-end-modal-footer">
        <ModalButton
          onClick={handleClose}
          text={'Продолжить'}
        />
      </div>
    </ModalWindow>
  );
};

export default RoundEndModal;
